import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";
import { HELP_OPTIONS, getUtm, postLead, track } from "../lib/api";

export const LeadForm = ({
  dark = false,
  source = "contact",
  formType = "contact",
  buttonLabel = "Let's Talk",
  testId = "lead-form",
}) => {
  const navigate = useNavigate();
  const started = useRef(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    zip_code: "",
    coverage_interests: [],
    concern: "",
    preferred_contact: "",
    marketing_consent: false,
    company: "",
  });

  const markStarted = () => {
    if (!started.current) {
      started.current = true;
      track("lead_form_started", { form_type: formType });
    }
  };

  const set = (key) => (e) => {
    markStarted();
    setForm({ ...form, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  };

  const toggleCoverage = (c) => {
    markStarted();
    setForm((f) => ({
      ...f,
      coverage_interests: f.coverage_interests.includes(c)
        ? f.coverage_interests.filter((x) => x !== c)
        : [...f.coverage_interests, c],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.phone.trim() || !form.email.trim()) {
      setError("Please add your first name, phone, and email so we can reach you.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await postLead({
        ...form,
        preferred_contact: form.preferred_contact || "Phone",
        source,
        form_type: formType,
        ...getUtm(),
      });
      track("lead_form_completed", { form_type: formType });
      navigate("/thank-you");
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Something went wrong sending that. Please call us instead."
      );
    } finally {
      setLoading(false);
    }
  };

  const inputCls = dark ? "input-field-dark" : "input-field";
  const labelCls = `block font-mono text-[11px] uppercase tracking-[0.18em] mb-1.5 ${dark ? "text-zinc-400" : "text-primer"}`;

  return (
    <form onSubmit={submit} data-testid={testId} className="space-y-5" noValidate>
      <input
        type="text"
        name="company"
        value={form.company}
        onChange={set("company")}
        className="hidden"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />

      {step === 1 && (
        <div>
      <fieldset>
        <legend className={labelCls}>What would you like help with?</legend>
        <div className="flex flex-wrap gap-2" data-testid={`${testId}-coverage-list`}>
          {HELP_OPTIONS.map((c) => {
            const active = form.coverage_interests.includes(c);
            return (
              <button
                type="button"
                key={c}
                data-testid={`${testId}-coverage-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => toggleCoverage(c)}
                aria-pressed={active}
                className={`chip min-h-[44px] ${
                  active
                    ? "border-lime bg-lime text-ink"
                    : dark
                      ? "border-line bg-ink-2 text-zinc-300 hover:border-lime/60"
                      : "border-ink/15 bg-white text-ink hover:border-lime"
                }`}
              >
                {active && <Check className="h-3.5 w-3.5" />}
                {c}
              </button>
            );
          })}
        </div>
      </fieldset>
          <button
            type="button"
            data-testid={`${testId}-continue`}
            onClick={() => {
              track("lead_step_completed", { form_type: formType, step: 1, interests: form.coverage_interests });
              setStep(2);
            }}
            className="btn-lime mt-6 w-full"
          >
            Continue <ArrowRight className="h-4 w-4" />
          </button>
          <p className={`mt-4 text-center text-xs ${dark ? "text-zinc-500" : "text-primer"}`}>
            No pressure. No obligation. Just a conversation about your options.
          </p>
        </div>
      )}

      {step === 2 && (
        <>
      <div className="flex items-center justify-between gap-4">
        <p className={`font-display text-lg font-bold uppercase tracking-wide ${dark ? "text-white" : "text-ink"}`}>
          How should we reach you?
        </p>
        <button
          type="button"
          data-testid={`${testId}-back`}
          onClick={() => setStep(1)}
          className={`shrink-0 font-mono text-xs uppercase tracking-wider transition-colors ${dark ? "text-zinc-400 hover:text-lime" : "text-primer hover:text-ink"}`}
        >
          Back
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor={`${testId}-first`} className={labelCls}>First name</label>
          <input id={`${testId}-first`} data-testid={`${testId}-first-name`} className={inputCls} value={form.first_name} onChange={set("first_name")} autoComplete="given-name" placeholder="First name" />
        </div>
        <div>
          <label htmlFor={`${testId}-last`} className={labelCls}>Last name</label>
          <input id={`${testId}-last`} data-testid={`${testId}-last-name`} className={inputCls} value={form.last_name} onChange={set("last_name")} autoComplete="family-name" placeholder="Last name" />
        </div>
        <div>
          <label htmlFor={`${testId}-phone`} className={labelCls}>Phone</label>
          <input id={`${testId}-phone`} data-testid={`${testId}-phone`} type="tel" className={inputCls} value={form.phone} onChange={set("phone")} autoComplete="tel" placeholder="(417) 555-0123" />
        </div>
        <div>
          <label htmlFor={`${testId}-email`} className={labelCls}>Email</label>
          <input id={`${testId}-email`} data-testid={`${testId}-email`} type="email" className={inputCls} value={form.email} onChange={set("email")} autoComplete="email" placeholder="you@email.com" />
        </div>
        <div>
          <label htmlFor={`${testId}-zip`} className={labelCls}>ZIP code</label>
          <input id={`${testId}-zip`} data-testid={`${testId}-zip`} inputMode="numeric" className={inputCls} value={form.zip_code} onChange={set("zip_code")} autoComplete="postal-code" placeholder="65804" />
        </div>
        <div>
          <label htmlFor={`${testId}-preferred`} className={labelCls}>Preferred way to reach you</label>
          <select id={`${testId}-preferred`} data-testid={`${testId}-preferred-contact`} className={inputCls} value={form.preferred_contact} onChange={set("preferred_contact")}>
            <option value="">Choose one (optional)</option>
            <option>Phone</option>
            <option>Text</option>
            <option>Email</option>
          </select>
        </div>
      </div>

      <div>
        <label htmlFor={`${testId}-concern`} className={labelCls}>What would you most like help understanding? (optional)</label>
        <textarea id={`${testId}-concern`} data-testid={`${testId}-concern`} rows={3} className={inputCls} value={form.concern} onChange={set("concern")} placeholder="Tell us what's on your mind — in your own words." />
      </div>

      <label className={`flex items-start gap-3 text-xs leading-relaxed ${dark ? "text-zinc-400" : "text-primer"}`}>
        <input
          type="checkbox"
          data-testid={`${testId}-consent`}
          checked={form.marketing_consent}
          onChange={set("marketing_consent")}
          className="mt-0.5 h-4 w-4 shrink-0 accent-[#A8E600]"
        />
        It&apos;s okay to send me occasional helpful insurance information. I understand submitting
        this form means a real person from Show Me Shield may contact me, and it does not obligate me
        to buy anything.
      </label>

      {error && (
        <p role="alert" data-testid={`${testId}-error`} className="rounded-sm border border-red-500/50 bg-red-500/10 px-4 py-3 text-sm font-medium text-red-600">
          {error}
        </p>
      )}

      <button
        type="submit"
        data-testid={`${testId}-submit`}
        disabled={loading}
        className="btn-lime w-full disabled:opacity-60"
      >
        {loading ? "Sending..." : buttonLabel} <ArrowRight className="h-4 w-4" />
      </button>
      <p className={`text-center text-xs ${dark ? "text-zinc-500" : "text-primer"}`}>
        No pressure. No obligation. Just a conversation about your options.
      </p>
        </>
      )}
    </form>
  );
};
